<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Trash2, Upload } from 'lucide-vue-next';
import { RouterLink } from 'vue-router';
import RichTextBlock from '@/components/blocks/RichTextBlock.vue';
import { useMutationConnectivity } from '@/composables/useConnectivity';
import { useSiteConfigStore } from '@/stores/siteConfig';
import { rememberHelpQueryPortal } from '@/utils/helpQueryPortalStorage';
import {
  fetchVapidPublicKey,
  serviceWorkerReadyForPush,
  subscribeHelpQueryPush,
} from '@/utils/webPush';

const { t, locale } = useI18n();

/** Matches `WEBFORM_ALLOWED_FILE_TYPES` labels in django_backend/home/models/form_models.py */
const WEBFORM_UPLOAD_TYPE_LABELS: Record<string, string> = {
  pdf: 'PDF',
  document: 'DOC, DOCX, ODT',
  image: 'JPG, JPEG, PNG, GIF',
  spreadsheet: 'XLS, XLSX, ODS, CSV',
};
const { mutationsBlocked } = useMutationConnectivity();

const API_BASE = '';
const WEB_FORM_SUBMIT_URL = `${API_BASE}/api/v2/web-form-submissions/`;
const PROCESS_BLOCK_SUBMIT_URL = `${API_BASE}/api/v2/process-block-submissions/`;
const NEED_HELP_BLOCK_SUBMIT_URL = `${API_BASE}/api/v2/need-help-block-submissions/`;

const siteConfigStore = useSiteConfigStore();

const NEED_HELP_PUSH_DEBUG = '[FormBlock][need-help-push]';

/** reCAPTCHA v3 site key (public): runtime API first, optional VITE_ for non-Docker local dev. */
const recaptchaSiteKey = computed(
  () =>
    siteConfigStore.recaptchaSiteKey ||
    (import.meta.env.VITE_RECAPTCHA_SITE_KEY as string)?.trim() ||
    ''
);

declare global {
  interface Window {
    grecaptcha?: {
      ready: (fn: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

let recaptchaReady: Promise<void> | null = null;
let recaptchaReadyForKey = '';

function loadRecaptcha(): Promise<void> {
  const key = recaptchaSiteKey.value;
  if (!key) return Promise.resolve();
  if (recaptchaReadyForKey !== key) {
    recaptchaReady = null;
    recaptchaReadyForKey = key;
  }
  if (recaptchaReady) return recaptchaReady;
  recaptchaReady = new Promise((resolve, reject) => {
    if (window.grecaptcha) {
      window.grecaptcha.ready(() => resolve());
      return;
    }
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(key)}`;
    script.async = true;
    script.onload = () => {
      window.grecaptcha?.ready(() => resolve());
    };
    script.onerror = () => reject(new Error('reCAPTCHA failed to load'));
    document.head.appendChild(script);
  });
  return recaptchaReady;
}

/** Form field definition returned by the backend form API. */
export interface FormFieldDef {
  clean_name: string;
  label: string;
  field_type: string;
  required: boolean;
  choices: string;
  default_value: string;
  help_text: string;
}

/** Block value: form object from API (get_api_representation). */
export interface FormBlockValue {
  form?: {
    id?: number | null;
    intro?: string;
    thank_you_text?: string;
    file_mode: string;
    form_fields: FormFieldDef[];
    upload_allowed_types?: string[];
    upload_accept?: string;
    upload_file_types_label?: string;
    upload_max_bytes?: number;
    upload_max_label?: string;
    /**
     * When set with page_id + block_id, submit to page-bound endpoints instead of web-form:
     * `page_block` → process-block-submissions; `need_help_block` → need-help-block-submissions.
     */
    submission?: {
      mode: 'page_block' | 'need_help_block';
      page_id?: number;
      block_id?: string;
    };
  } | null;
}

const props = withDefaults(
  defineProps<{
    form?: FormBlockValue['form'];
    /** When set, the form element gets this id (for external submit buttons via form attribute). */
    formId?: string;
    /** When true, the default submit button is hidden (e.g. when submit is provided by parent). */
    hideDefaultSubmit?: boolean;
  }>(),
  { hideDefaultSubmit: false }
);

const emit = defineEmits<{
  submitting: [value: boolean];
  submitted: [];
}>();

const submitted = ref(false);
/** After Need help submit: optional Web Push opt-in (token from API). */
const needHelpPushOptIn = ref<{ submissionId: number; token: string } | null>(null);
const needHelpPushLoading = ref(false);
const needHelpPushError = ref<string | null>(null);
const needHelpPushDone = ref(false);
const submitError = ref<string | null>(null);
const fieldErrors = ref<Record<string, string>>({});
const submitting = ref(false);
const formValues = ref<Record<string, string>>({});

const formFields = computed(() => {
  const fields = props.form?.form_fields;
  return Array.isArray(fields) ? fields : [];
});

const fileMode = computed(() => props.form?.file_mode ?? 'no_files');
const hasOneFile = computed(() => fileMode.value === 'one_file');
const hasMultipleFiles = computed(() => fileMode.value === 'multiple_files');

/** Upload config from backend (only relevant when hasOneFile or hasMultipleFiles). */
const uploadAccept = computed(() => props.form?.upload_accept ?? '');
const uploadFileTypesLabel = computed(() => {
  void locale.value;
  const keys = props.form?.upload_allowed_types;
  if (Array.isArray(keys) && keys.length > 0) {
    const labels = keys
      .map((key) => WEBFORM_UPLOAD_TYPE_LABELS[key])
      .filter((label): label is string => Boolean(label));
    if (labels.length > 0) {
      return t('form.uploadFileTypesHint', { types: labels.join(', ') });
    }
  }
  return props.form?.upload_file_types_label ?? '';
});
const uploadMaxLabel = computed(() => props.form?.upload_max_label ?? '');

const fileInputRef = ref<HTMLInputElement | null>(null);
const filesInputRef = ref<HTMLInputElement | null>(null);

/** File list for display (names) from the active file input. */
const selectedFileNames = ref<string[]>([]);

function getActiveFileInput(): HTMLInputElement | null {
  if (hasOneFile.value) return fileInputRef.value;
  if (hasMultipleFiles.value) return filesInputRef.value;
  return null;
}

function updateSelectedFileNames() {
  const input = getActiveFileInput();
  if (!input?.files?.length) {
    selectedFileNames.value = [];
    return;
  }
  selectedFileNames.value = Array.from(input.files).map((f) => f.name);
}

function onFileInputChange() {
  updateSelectedFileNames();
}

function openFilePicker() {
  getActiveFileInput()?.click();
}

function onDropzoneDrop(e: DragEvent) {
  e.preventDefault();
  const input = getActiveFileInput();
  const dropped = e.dataTransfer?.files;
  if (!input || !dropped?.length) return;
  const dt = new DataTransfer();
  for (const file of Array.from(dropped)) dt.items.add(file);
  input.files = dt.files;
  updateSelectedFileNames();
}

function onDropzoneDragover(e: DragEvent) {
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
}

/** Remove file at index from the current selection and sync back to the file input. */
function removeFileAtIndex(index: number) {
  const input = getActiveFileInput();
  if (!input?.files?.length) return;
  const files = Array.from(input.files);
  if (index < 0 || index >= files.length) return;
  const remaining = files.filter((_, i) => i !== index);
  const dt = new DataTransfer();
  for (const f of remaining) dt.items.add(f);
  input.files = dt.files;
  updateSelectedFileNames();
}

/** Stable id for multiline v-model state: only reset when the form or its fields actually change, not when the parent passes a new array reference for the same form. */
function webFormFieldsSignature(form: FormBlockValue['form'] | undefined): string {
  if (!form || !Array.isArray(form.form_fields)) return '';
  const names = form.form_fields.map((f) => f.clean_name).join('\0');
  const sm = form.submission;
  if (
    (sm?.mode === 'page_block' || sm?.mode === 'need_help_block') &&
    typeof sm.page_id === 'number' &&
    sm.block_id
  ) {
    return `${sm.mode}:${sm.page_id}:${sm.block_id}:${names}`;
  }
  if (form.id == null || typeof form.id !== 'number') return '';
  return `${form.id}:${names}`;
}

watch(
  () => webFormFieldsSignature(props.form),
  (sig, prevSig) => {
    if (!sig) {
      if (props.form == null) {
        formValues.value = {};
      }
      return;
    }
    if (sig === prevSig) return;
    const fields = props.form?.form_fields;
    if (!Array.isArray(fields)) return;
    const next: Record<string, string> = {};
    for (const f of fields) next[f.clean_name] = f.default_value ?? '';
    formValues.value = next;
  },
  { immediate: true }
);

function choiceOptions(choices: string): string[] {
  if (!choices || typeof choices !== 'string') return [];
  return choices
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function isChoiceField(fieldType: string): boolean {
  return ['dropdown', 'radio', 'checkboxes', 'multiselect'].includes(fieldType);
}

function validateRequiredFileUploads(): boolean {
  if (hasOneFile.value) {
    const input = fileInputRef.value;
    if (!input?.files?.length) {
      fieldErrors.value = { file: t('form.fileRequired') };
      submitError.value = t('form.correctErrorsBelow');
      return false;
    }
  }
  if (hasMultipleFiles.value) {
    const input = filesInputRef.value;
    if (!input?.files?.length) {
      fieldErrors.value = { files: t('form.filesRequired') };
      submitError.value = t('form.correctErrorsBelow');
      return false;
    }
  }
  return true;
}

async function onSubmit(event: Event) {
  event.preventDefault();
  if (mutationsBlocked.value) return;
  const formEl = event.target as HTMLFormElement;
  const f = props.form;
  const pageBoundMode = f?.submission?.mode;
  const pageBlock =
    (pageBoundMode === 'page_block' || pageBoundMode === 'need_help_block') &&
    typeof f?.submission?.page_id === 'number' &&
    f.submission.block_id;
  const formId = f?.id;
  if (
    !pageBlock &&
    (formId == null || typeof formId !== 'number')
  ) {
    submitError.value = 'Form is missing.';
    return;
  }
  submitError.value = null;
  fieldErrors.value = {};
  if (!validateRequiredFileUploads()) {
    return;
  }
  submitting.value = true;
  emit('submitting', true);
  try {
    const formData = new FormData(formEl);
    let submitUrl = WEB_FORM_SUBMIT_URL;
    if (pageBlock) {
      formData.set('page_id', String(f!.submission!.page_id));
      formData.set('block_id', String(f!.submission!.block_id));
      submitUrl =
        pageBoundMode === 'need_help_block' ? NEED_HELP_BLOCK_SUBMIT_URL : PROCESS_BLOCK_SUBMIT_URL;
    } else {
      formData.set('web_form_id', String(formId));
    }
    console.info(`${NEED_HELP_PUSH_DEBUG} posting`, {
      submitUrl,
      pageBoundMode,
      pageBlock,
      formSubmissionMeta: f?.submission ?? null,
    });
    const siteKey = recaptchaSiteKey.value;
    if (siteKey) {
      await loadRecaptcha();
      const token = await window.grecaptcha!.execute(siteKey, { action: 'submit' });
      formData.set('recaptcha_token', token);
    }

    const response = await fetch(submitUrl, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
    });
    const data = (await response.json().catch(() => ({}))) as {
      success?: boolean;
      errors?: Record<string, string | string[]>;
      error?: string;
      submission_id?: number;
      submission_model?: string;
      push_subscribe_token?: string;
    };
    if (response.ok && data.success) {
      const tok =
        typeof data.push_subscribe_token === 'string' ? data.push_subscribe_token.trim() : '';
      const sid = data.submission_id;
      const gate = {
        sidIsNumber: typeof sid === 'number',
        sidValue: sid,
        tokenNonEmpty: Boolean(tok),
        tokenLength: tok.length,
        submission_model: data.submission_model,
        modelMatches: data.submission_model === 'HelpQuerySubmission',
        pageBoundMode,
      };
      console.info(`${NEED_HELP_PUSH_DEBUG} submit OK`, {
        submitUrl,
        importMetaEnv: {
          MODE: import.meta.env.MODE,
          DEV: import.meta.env.DEV,
          PROD: import.meta.env.PROD,
        },
        gate,
        pushPayloadWouldShow:
          gate.sidIsNumber && gate.tokenNonEmpty && gate.modelMatches,
      });
      // Trust the API: token is only returned for Help Query persistence. Do not require
      // form.submission.mode === 'need_help_block' (CMS payload can omit or vary that shape).
      // Set opt-in BEFORE submitted so watchers/DOM see token state when thank-you renders.
      if (
        typeof sid === 'number' &&
        tok &&
        data.submission_model === 'HelpQuerySubmission'
      ) {
        needHelpPushOptIn.value = { submissionId: sid, token: tok };
        rememberHelpQueryPortal(sid, tok);
        console.info(`${NEED_HELP_PUSH_DEBUG} needHelpPushOptIn SET`, needHelpPushOptIn.value);
      } else {
        console.warn(`${NEED_HELP_PUSH_DEBUG} needHelpPushOptIn NOT set — gate failed`, gate);
      }
      submitted.value = true;
      emit('submitted');
      return;
    }
    console.warn(`${NEED_HELP_PUSH_DEBUG} submit response not OK/success`, {
      httpOk: response.ok,
      status: response.status,
      dataSuccess: data.success,
      keys: Object.keys(data),
    });
    if (response.status === 400 && data.errors) {
      fieldErrors.value = Object.fromEntries(
        Object.entries(data.errors).map(([k, v]) => [k, Array.isArray(v) ? v.join(' ') : v])
      ) as Record<string, string>;
      submitError.value = 'Please correct the errors below.';
      return;
    }
    submitError.value = data.errors?.__all__?.[0] ?? data.error ?? 'Submission failed. Please try again.';
  } catch (err) {
    console.error('Form submission failed:', err);
    submitError.value = 'Network error. Please try again.';
  } finally {
    submitting.value = false;
    emit('submitting', false);
  }
}

watch(submitted, (isThankYou) => {
  if (!isThankYou) return;
  console.info(`${NEED_HELP_PUSH_DEBUG} thank-you template branch`, {
    needHelpPushOptIn: needHelpPushOptIn.value,
    needHelpPushDone: needHelpPushDone.value,
    showPushBlock:
      Boolean(needHelpPushOptIn.value) && !needHelpPushDone.value,
  });
});

async function onNeedHelpPushEnable() {
  const o = needHelpPushOptIn.value;
  needHelpPushError.value = null;
  if (!o) return;
  console.info(`${NEED_HELP_PUSH_DEBUG} enable clicked`, { submissionId: o.submissionId });
  if (!(await serviceWorkerReadyForPush())) {
    needHelpPushError.value = t('account.dashboard.workflowPushNeedsSw');
    return;
  }
  let vapid = siteConfigStore.vapidPublicKey;
  if (!vapid) {
    vapid = (await fetchVapidPublicKey()) ?? '';
  }
  if (!vapid) {
    needHelpPushError.value = t('account.dashboard.workflowPushUnavailable');
    return;
  }
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    needHelpPushError.value = t('account.dashboard.workflowPushError');
    return;
  }
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') {
    needHelpPushError.value = t('account.dashboard.workflowPushError');
    return;
  }
  needHelpPushLoading.value = true;
  try {
    const reg = await navigator.serviceWorker.ready;
    const ok = await subscribeHelpQueryPush(reg, vapid, o.submissionId, o.token);
    if (ok) {
      needHelpPushDone.value = true;
    } else {
      needHelpPushError.value = t('account.dashboard.workflowPushError');
    }
  } catch {
    needHelpPushError.value = t('account.dashboard.workflowPushError');
  } finally {
    needHelpPushLoading.value = false;
  }
}
</script>

<template>
  <section class="block-form">
    <template v-if="!form">
      <p class="form-missing">Form not available.</p>
    </template>

    <template v-else-if="submitted">
      <div class="form-thank-you" role="status">
        <RichTextBlock v-if="form.thank_you_text" :html="form.thank_you_text" />
        <p v-else>{{ t('form.thankYou') }}</p>
        <div v-if="needHelpPushOptIn" class="form-thank-you-portal">
          <RouterLink
            class="form-thank-you-portal__link"
            :to="{
              name: 'help-query-status',
              query: {
                submission_id: String(needHelpPushOptIn.submissionId),
                push_token: needHelpPushOptIn.token,
              },
            }"
          >
            {{ t('account.dashboard.helpQueryPortalThankYouLink') }}
          </RouterLink>
          <p class="form-thank-you-portal__hint">
            {{ t('account.dashboard.helpQueryPortalThankYouHint') }}
          </p>
        </div>
        <div v-if="needHelpPushOptIn && !needHelpPushDone" class="form-thank-you-push">
          <p class="form-thank-you-push__prompt">{{ t('account.dashboard.needHelpPushPrompt') }}</p>
          <p v-if="needHelpPushError" class="form-error" role="alert">{{ needHelpPushError }}</p>
          <button
            type="button"
            class="form-thank-you-push__btn"
            :disabled="needHelpPushLoading"
            @click="onNeedHelpPushEnable"
          >
            {{
              needHelpPushLoading
                ? t('account.dashboard.mySubmissionsLoading')
                : t('account.dashboard.needHelpPushButton')
            }}
          </button>
        </div>
        <p v-if="needHelpPushDone" class="form-thank-you-push__done">
          {{ t('account.dashboard.needHelpPushDone') }}
        </p>
      </div>
    </template>

    <template v-else>
      <div v-if="form.intro" class="form-intro">
        <RichTextBlock :html="form.intro" />
      </div>

      <form
        :id="formId || undefined"
        class="form-fields"
        method="post"
        @submit="onSubmit"
        enctype="multipart/form-data"
      >
        <p v-if="submitError" class="form-error" role="alert">{{ submitError }}</p>
        <div
          v-for="field in formFields"
          :key="field.clean_name"
          class="form-field"
          :class="{
            'form-field--choice': isChoiceField(field.field_type),
            'form-field--error': fieldErrors[field.clean_name],
          }"
        >
          <template v-if="field.field_type === 'hidden'">
            <input
              :id="`field-${field.clean_name}`"
              type="hidden"
              :name="field.clean_name"
              :value="field.default_value"
            />
          </template>

          <template v-else>
            <label :for="`field-${field.clean_name}`" class="form-label">
              {{ field.label }}
              <span v-if="field.required" class="form-required" aria-hidden="true">*</span>
            </label>
            <p v-if="field.help_text" class="form-help">{{ field.help_text }}</p>
            <p v-if="fieldErrors[field.clean_name]" class="form-field-error" role="alert">
              {{ fieldErrors[field.clean_name] }}
            </p>

            <input
              v-if="['singleline', 'email', 'number', 'url', 'date', 'datetime'].includes(field.field_type)"
              :id="`field-${field.clean_name}`"
              v-model="formValues[field.clean_name]"
              :name="field.clean_name"
              :type="
                field.field_type === 'singleline'
                  ? 'text'
                  : field.field_type === 'datetime'
                    ? 'datetime-local'
                    : field.field_type
              "
              :required="field.required"
              class="form-input"
            />

            <textarea
              v-else-if="field.field_type === 'multiline'"
              :id="`field-${field.clean_name}`"
              v-model="formValues[field.clean_name]"
              :name="field.clean_name"
              :required="field.required"
              class="form-input form-textarea"
              rows="4"
            />

            <input
              v-else-if="field.field_type === 'checkbox'"
              :id="`field-${field.clean_name}`"
              :name="field.clean_name"
              type="checkbox"
              :value="field.default_value || 'on'"
              class="form-input form-checkbox"
            />

            <select
              v-else-if="field.field_type === 'dropdown'"
              :id="`field-${field.clean_name}`"
              :name="field.clean_name"
              :required="field.required"
              class="form-input form-select"
            >
              <option value="">—</option>
              <option
                v-for="opt in choiceOptions(field.choices)"
                :key="opt"
                :value="opt"
                :selected="opt === field.default_value"
              >
                {{ opt }}
              </option>
            </select>

            <select
              v-else-if="field.field_type === 'multiselect'"
              :id="`field-${field.clean_name}`"
              :name="field.clean_name"
              multiple
              class="form-input form-select"
            >
              <option
                v-for="opt in choiceOptions(field.choices)"
                :key="opt"
                :value="opt"
                :selected="
                  (field.default_value || '')
                    .split(/[\n,]+/)
                    .map((s) => s.trim())
                    .includes(opt)
                "
              >
                {{ opt }}
              </option>
            </select>

            <div v-else-if="field.field_type === 'radio'" class="form-choices">
              <label
                v-for="opt in choiceOptions(field.choices)"
                :key="opt"
                class="form-choice-label"
              >
                <input
                  type="radio"
                  :name="field.clean_name"
                  :value="opt"
                  :required="field.required"
                  class="form-radio"
                />
                {{ opt }}
              </label>
            </div>

            <div v-else-if="field.field_type === 'checkboxes'" class="form-choices">
              <label
                v-for="opt in choiceOptions(field.choices)"
                :key="opt"
                class="form-choice-label"
              >
                <input type="checkbox" :name="field.clean_name" :value="opt" class="form-checkbox" />
                {{ opt }}
              </label>
            </div>

            <input
              v-else
              :id="`field-${field.clean_name}`"
              v-model="formValues[field.clean_name]"
              :name="field.clean_name"
              type="text"
              :required="field.required"
              class="form-input"
            />
          </template>
        </div>

        <div v-if="hasOneFile" class="form-field" :class="{ 'form-field--error': fieldErrors['file'] }">
          <label for="webform-file" class="form-label">
            {{ t('form.fileLabel') }} <span class="form-required" aria-hidden="true">*</span>
          </label>
          <p v-if="fieldErrors['file']" class="form-field-error" role="alert">{{ fieldErrors['file'] }}</p>
          <div
            class="form-upload-dropzone"
            role="button"
            tabindex="0"
            :aria-label="t('form.uploadOneFileAriaLabel')"
            @click="openFilePicker"
            @drop.prevent="onDropzoneDrop"
            @dragover.prevent="onDropzoneDragover"
            @keydown.enter.prevent="openFilePicker"
            @keydown.space.prevent="openFilePicker"
          >
            <Upload class="form-upload-icon" aria-hidden="true" />
            <span class="form-upload-prompt-wrap">
              <span class="form-upload-prompt">{{ t('form.clickToUpload') }}</span>
              <span class="form-upload-prompt-suffix">{{ t('form.orDragAndDrop') }}</span>
            </span>
            <p v-if="uploadFileTypesLabel || uploadMaxLabel" class="form-upload-hint">
              <template v-if="uploadFileTypesLabel">{{ uploadFileTypesLabel }}</template>
              <template v-if="uploadMaxLabel">{{ t('form.maxHint', { max: uploadMaxLabel }) }}</template>
            </p>
            <div v-if="selectedFileNames.length" class="form-upload-selected-wrap">
              <p class="form-upload-selected-count">{{ t('form.filesSelectedCount', { count: selectedFileNames.length }) }}</p>
              <ul class="form-upload-selected" aria-live="polite">
                <li
                  v-for="(name, i) in selectedFileNames"
                  :key="i"
                  class="form-upload-selected-item"
                >
                  <span class="form-upload-selected-name">{{ name }}</span>
                  <button
                    type="button"
                    class="form-upload-remove"
                    :aria-label="t('form.removeFileAriaLabel')"
                    @click.stop.prevent="removeFileAtIndex(i)"
                  >
                    <Trash2 class="form-upload-remove-icon" aria-hidden="true" />
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <input
            ref="fileInputRef"
            id="webform-file"
            type="file"
            name="file"
            class="form-upload-input"
            :accept="uploadAccept"
            required
            aria-hidden="true"
            @change="onFileInputChange"
          />
        </div>
        <div v-if="hasMultipleFiles" class="form-field" :class="{ 'form-field--error': fieldErrors['files'] }">
          <label for="webform-files" class="form-label">
            {{ t('form.filesLabel') }} <span class="form-required" aria-hidden="true">*</span>
          </label>
          <p v-if="fieldErrors['files']" class="form-field-error" role="alert">{{ fieldErrors['files'] }}</p>
          <div
            class="form-upload-dropzone"
            role="button"
            tabindex="0"
            :aria-label="t('form.uploadAriaLabel')"
            @click="openFilePicker"
            @drop.prevent="onDropzoneDrop"
            @dragover.prevent="onDropzoneDragover"
            @keydown.enter.prevent="openFilePicker"
            @keydown.space.prevent="openFilePicker"
          >
            <Upload class="form-upload-icon" aria-hidden="true" />
            <span class="form-upload-prompt-wrap">
              <span class="form-upload-prompt">{{ t('form.clickToUpload') }}</span>
              <span class="form-upload-prompt-suffix">{{ t('form.orDragAndDrop') }}</span>
            </span>
            <p v-if="uploadFileTypesLabel || uploadMaxLabel" class="form-upload-hint">
              <template v-if="uploadFileTypesLabel">{{ uploadFileTypesLabel }}</template>
              <template v-if="uploadMaxLabel">{{ t('form.maxHint', { max: uploadMaxLabel }) }}</template>
            </p>
            <div v-if="selectedFileNames.length" class="form-upload-selected-wrap">
              <p class="form-upload-selected-count">{{ t('form.filesSelectedCount', { count: selectedFileNames.length }) }}</p>
              <ul class="form-upload-selected" aria-live="polite">
                <li
                  v-for="(name, i) in selectedFileNames"
                  :key="i"
                  class="form-upload-selected-item"
                >
                  <span class="form-upload-selected-name">{{ name }}</span>
                  <button
                    type="button"
                    class="form-upload-remove"
                    :aria-label="t('form.removeFileAriaLabel')"
                    @click.stop.prevent="removeFileAtIndex(i)"
                  >
                    <Trash2 class="form-upload-remove-icon" aria-hidden="true" />
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <input
            ref="filesInputRef"
            id="webform-files"
            type="file"
            name="files"
            multiple
            class="form-upload-input"
            :accept="uploadAccept"
            required
            aria-hidden="true"
            @change="onFileInputChange"
          />
        </div>

        <div v-if="!hideDefaultSubmit" class="form-actions">
          <div v-if="mutationsBlocked" class="form-offline-callout" role="status">
            <p class="form-offline-callout__title">{{ t('common.connectionRequired') }}</p>
            <p class="form-offline-callout__detail">{{ t('common.offlineMutationsHint') }}</p>
          </div>
          <button type="submit" class="form-submit" :disabled="submitting || mutationsBlocked">
            {{ submitting ? t('form.submitting') : t('form.submit') }}
          </button>
        </div>
      </form>
    </template>
  </section>
</template>

<style scoped>
.block-form {
  margin: 1.5rem 0;
}
.form-missing {
  color: var(--color-text-muted, #666);
}
.form-intro {
  margin-bottom: 1rem;
}
.form-thank-you {
  margin-top: 1rem;
}
.form-thank-you-portal {
  margin-top: 1.25rem;
  padding: 0.9rem 1rem;
  border-radius: 8px;
  border: 1px solid color-mix(in srgb, var(--color-primary, #0066cc) 25%, var(--color-border, #e5e5e5));
  background: color-mix(in srgb, var(--color-primary, #0066cc) 6%, transparent);
}
.form-thank-you-portal__link {
  display: inline-block;
  font-weight: 600;
  color: var(--color-primary, #0066cc);
  text-decoration: none;
}
.form-thank-you-portal__link:hover {
  text-decoration: underline;
}
.form-thank-you-portal__hint {
  margin: 0.5rem 0 0;
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--color-text-muted, #666);
}
.form-thank-you-push {
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid var(--color-border, #e5e5e5);
}
.form-thank-you-push__prompt {
  margin: 0 0 0.5rem;
  font-size: 0.95rem;
}
.form-thank-you-push__btn {
  padding: 0.45rem 0.85rem;
  font: inherit;
  cursor: pointer;
  border-radius: 6px;
  border: 1px solid var(--color-primary, #0066cc);
  background: var(--color-primary, #0066cc);
  color: #fff;
}
.form-thank-you-push__btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.form-thank-you-push__done {
  margin-top: 1rem;
  font-size: 0.95rem;
  color: var(--color-text-muted, #444);
}
.form-fields {
  margin-top: 1rem;
  width: 100%;
}
.form-offline-callout {
  margin: 0 0 0.75rem;
  padding: 0.65rem 0.75rem;
  border-radius: 0.375rem;
  border: 1px solid var(--color-border, #e4e4e7);
  background: var(--color-surface-muted, #fafafa);
}
.form-offline-callout__title {
  margin: 0 0 0.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--color-text, #18181b);
}
.form-offline-callout__detail {
  margin: 0;
  font-size: 0.8125rem;
  line-height: 1.45;
  color: var(--color-text-muted, #52525b);
}
.form-field {
  margin-bottom: 1.25rem;
  width: 100%;
}
.form-field--choice .form-choices {
  margin-top: 0.25rem;
}
.form-label {
  display: block;
  width: 100%;
  font-weight: 600;
  margin-bottom: 0.25rem;
}
.form-required {
  color: var(--color-required, #c00);
}
.form-help {
  font-size: 0.875rem;
  color: var(--color-text-muted, #666);
  margin: 0 0 0.25rem;
}
.form-error {
  color: var(--color-error, #c00);
  margin-bottom: 1rem;
}
.form-field-error {
  font-size: 0.875rem;
  color: var(--color-error, #c00);
  margin: 0.25rem 0 0;
}
.form-input {
  width: 100%;
  max-width: 100%;
  padding: 0.5rem 0.5rem;
  font: inherit;
  border: 1px solid var(--color-border, #ccc);
  border-radius: 4px;
}
.form-textarea {
  min-height: 6rem;
  resize: vertical;
}
.form-select {
  min-height: 2.5rem;
}
.form-checkbox,
.form-radio {
  width: auto;
  margin-right: 0.5rem;
}
.form-choices {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.form-choice-label {
  display: flex;
  align-items: center;
  font-weight: normal;
  cursor: pointer;
}
.form-actions {
  margin-top: 1.5rem;
}
.form-submit {
  padding: 0.5rem 1rem;
  font: inherit;
  background: var(--color-button-bg, #333);
  color: var(--color-button-text, #fff);
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.form-submit:hover {
  opacity: 0.9;
}
.form-submit:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

/* File upload dropzone: matches Prototype visual (border-2 dashed gray-300, rounded-lg, p-8, gradient, shadow-inner). */
.form-upload-dropzone {
  border: 2px dashed #d1d5db;
  border-radius: 0.5rem;
  padding: 2rem;
  min-height: 10.5rem;
  background: linear-gradient(to bottom right, #f9fafb, #fff);
  box-shadow: inset 0 2px 4px 0 rgb(0 0 0 / 0.05);
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}
.form-upload-dropzone:hover {
  border-color: #9ca3af;
  background: linear-gradient(to bottom right, #f3f4f6, #fafafa);
}
.form-upload-dropzone:focus {
  outline: 2px solid var(--color-button-bg, #333);
  outline-offset: 2px;
}
.form-upload-icon {
  width: 2.5rem;
  height: 2.5rem;
  color: #9ca3af;
  margin: 0 auto 0.75rem;
  display: block;
}
.form-upload-prompt-wrap {
  display: block;
}
.form-upload-prompt {
  font-weight: 500;
  color: var(--color-primary, #047857);
}
.form-upload-dropzone:hover .form-upload-prompt {
  text-decoration: underline;
}
.form-upload-prompt-suffix {
  color: #4b5563;
}
.form-upload-hint {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0.25rem 0 0;
}
/* Selected files list: matches Prototype (mt-4 text-sm text-gray-700, count line, list with remove). */
.form-upload-selected-wrap {
  margin-top: 1rem;
  font-size: 0.875rem;
  color: #374151;
  text-align: left;
}
.form-upload-selected-count {
  font-weight: 500;
  margin: 0 0 0.5rem;
}
.form-upload-selected {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.form-upload-selected-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  background: #f9fafb;
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
}
.form-upload-selected-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.form-upload-remove {
  flex-shrink: 0;
  margin-left: 0.5rem;
  padding: 0.25rem;
  border: 0;
  background: transparent;
  color: #4b5563;
  cursor: pointer;
  border-radius: 0.25rem;
}
.form-upload-remove:hover {
  color: #111827;
}
.form-upload-remove-icon {
  width: 1rem;
  height: 1rem;
  display: block;
}
.form-upload-input {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
